'use client';

import { useMemo } from 'react';
import { Box, Container, Grid, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core';
import type { Locale } from '../../i18n/locales';
import { pickLocaleString } from '../../lib/content/localization';
import { resolvePublicHref } from '../../lib/content/comparisonLinks';
import { Header } from '../layout/Header';
import { Footer } from '../layout/Footer';
import { PublicPageShell } from '../layout/PublicPageShell';
import type { ProductPageData } from '../../types/content';
import { HeroBlock } from './blocks/HeroBlock';
import { CoverageAccordionBlock } from './blocks/CoverageAccordionBlock';
import { FeatureHighlightBlock } from './blocks/FeatureHighlightBlock';
import { useRealtimeContent } from '../../hooks/useRealtimeContent';
import { useRealtimeRefresh } from '../../hooks/useRealtimeRefresh';
import { useSiteSettings } from '../providers/RealtimeSiteSettingsProvider';
import { AutovakuutusSidebar } from './AutovakuutusSidebar';
import type { AutovakuutusSubpageLink } from '../../lib/content/autovakuutus';

type TextBlock = {
  heading?: string;
  title?: string;
  body?: string;
};

export function CarInsuranceView({
  data: initialData,
  locale,
  autovakuutusSubpages,
}: {
  data: ProductPageData;
  locale: Locale;
  autovakuutusSubpages?: AutovakuutusSubpageLink[];
}) {
  const siteSettings = useSiteSettings();

  const data = useRealtimeContent<ProductPageData>(
    initialData,
    locale,
    ['car_content', 'car_sections', 'car_price_factors', 'car_comparison_rows', 'car_faqs'],
    (prev, table, rows) => {
      switch (table) {
        case 'car_content': {
          const row = rows[0];
          if (!row) return prev;
          return {
            ...prev,
            title: row.title ?? prev.title,
            ingress: row.body ?? prev.ingress,
            heroSubtitle: row.hero_subtitle ?? prev.heroSubtitle,
            ctaLabel: row.primary_cta_label ?? row.eyebrow ?? prev.ctaLabel,
            secondaryCtaLabel: row.secondary_cta_label ?? prev.secondaryCtaLabel,
            secondaryCtaHref: row.secondary_cta_href ?? prev.secondaryCtaHref,
            heroImageUrl: row.hero_image_url ?? prev.heroImageUrl,
            heroImageAlt: row.hero_image_alt ?? prev.heroImageAlt,
          };
        }
        case 'car_sections':
          return {
            ...prev,
            sections: rows.map((r) => ({
              heading: r.title || r.heading || '',
              body: r.body || '',
            })),
          };
        case 'car_price_factors':
          return {
            ...prev,
            priceFactors: rows.map((r) => ({
              title: r.title || '',
              body: r.body || '',
            })),
          };
        case 'car_faqs':
          return {
            ...prev,
            faqs: rows.map((r) => ({
              question: r.question || '',
              answer: r.answer || '',
            })),
          };
        default:
          return prev;
      }
    },
  );

  const refreshSubscriptions = useMemo(
    () => [
      { table: 'car_content', filter: `locale=eq.${locale}` },
      { table: 'car_sections', filter: `locale=eq.${locale}` },
      { table: 'car_price_factors', filter: `locale=eq.${locale}` },
      { table: 'car_comparison_rows', filter: `locale=eq.${locale}` },
      { table: 'car_faqs', filter: `locale=eq.${locale}` },
      { table: 'site_navigation_children', filter: 'parent_href=eq./autovakuutus' },
    ],
    [locale],
  );
  useRealtimeRefresh(refreshSubscriptions);

  const coverageItems = ((data.sections as TextBlock[]) || []).map((section) => ({
    title: section.heading || section.title || '',
    description: section.body || '',
  }));

  const highlightItems = ((data.priceFactors as TextBlock[]) || []).map((factor, idx: number) => ({
    title: factor.title || '',
    description: factor.body || '',
    imageOnLeft: idx % 2 !== 0,
  }));

  const faqItems = (data.faqs || []).map((faq) => ({
    title: faq.question || '',
    description: faq.answer || '',
  }));

  const leadItem = coverageItems[0];
  const tailItems = coverageItems.slice(1);

  return (
    <PublicPageShell>
      <Header locale={locale} settings={siteSettings} />

      <HeroBlock
        title={data.title}
        subtitle={data.heroSubtitle || data.ingress}
        primaryCtaLabel={data.ctaLabel}
        primaryCtaHref={resolvePublicHref(locale, '/vertailu/autovakuutus')}
        secondaryCtaLabel={data.secondaryCtaLabel}
        secondaryCtaHref={data.secondaryCtaHref ? resolvePublicHref(locale, data.secondaryCtaHref) : undefined}
        trustLabel={!data.secondaryCtaHref ? data.secondaryCtaLabel : undefined}
        backgroundImage={data.heroImageUrl}
        locale={locale}
      />

      <Container size="xl" py={56}>
        <Grid gutter={20} align="start">
          <Grid.Col span={{ base: 12, md: autovakuutusSubpages?.length ? 8 : 12 }}>
            <Stack gap={16}>
              {leadItem ? (
                <Paper p={{ base: 24, md: 34 }} radius={40} withBorder style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-base)' }}>
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing={24}>
                    <Group gap={14} align="start" wrap="nowrap">
                      <Box
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 999,
                          background: 'var(--brand-primary)',
                          color: '#fff',
                          display: 'grid',
                          placeItems: 'center',
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        −
                      </Box>
                      <Text style={{ color: 'var(--text-primary)', fontSize: 'clamp(1.8rem, 3.8vw, 2.5rem)', lineHeight: 1.3, fontWeight: 500 }}>
                        {leadItem.title || data.title}
                      </Text>
                    </Group>

                    <Stack gap={10}>
                      <Text style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }}>{leadItem.description}</Text>
                      {tailItems.length ? (
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={8}>
                          {tailItems.slice(0, 6).map((item, idx) => (
                            <Text key={`${item.title}-${idx}`} size="sm" style={{ color: 'var(--text-secondary)' }}>
                              • {item.title}
                            </Text>
                          ))}
                        </SimpleGrid>
                      ) : null}
                    </Stack>
                  </SimpleGrid>
                </Paper>
              ) : null}

              {tailItems.map((item, idx) => (
                <Paper
                  key={`${item.title}-${idx}`}
                  p={{ base: 20, md: 30 }}
                  radius={999}
                  withBorder
                  style={{
                    borderColor: 'var(--surface-border)',
                    background: idx % 3 === 0 ? 'var(--surface-muted)' : idx % 3 === 1 ? 'var(--surface-alt)' : 'var(--surface-soft)',
                  }}
                >
                  <Group gap={14}>
                    <Box
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 999,
                        background: 'var(--brand-primary)',
                        color: '#fff',
                        display: 'grid',
                        placeItems: 'center',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      +
                    </Box>
                    <Text style={{ color: 'var(--text-primary)', fontSize: 'clamp(1.4rem, 3vw, 2.2rem)', lineHeight: 1.3, fontWeight: 500 }}>
                      {item.title}
                    </Text>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Grid.Col>

          {autovakuutusSubpages?.length ? (
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Box style={{ position: 'sticky', top: 100 }}>
                <AutovakuutusSidebar locale={locale} items={autovakuutusSubpages} />
              </Box>
            </Grid.Col>
          ) : null}
        </Grid>
      </Container>

      {highlightItems.length ? <FeatureHighlightBlock items={highlightItems} /> : null}

      {faqItems.length ? (
        <CoverageAccordionBlock
          title={
            pickLocaleString(
              {
                fi: 'Usein kysytyt kysymykset autovakuutuksesta',
                sv: 'Vanliga frågor om bilförsäkring',
                en: 'Frequently asked questions about car insurance',
              },
              locale,
            )
          }
          items={faqItems}
        />
      ) : null}

      <Footer locale={locale} settings={siteSettings} />
    </PublicPageShell>
  );
}
